import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, Loader2, UserRound, X, ZoomIn, ZoomOut } from "lucide-react";
import { supabase } from "../lib/supabase";

// Same private signed-URL method as the rest of the app; short expiry, always signed on demand.
const sign = (path) =>
  supabase.storage.from("profile-photos").createSignedUrl(path, 600).then(({ data, error }) => (error || !data?.signedUrl ? null : data.signedUrl));

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const UNAVAILABLE = "This photo is no longer available";

export function PhotoViewer({ photos, index, onClose }) {
  const n = photos.length;
  const [i, setI] = useState(index);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dlBusy, setDlBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const pointers = useRef(new Map());
  const gesture = useRef({});

  const go = (d) => setI((v) => (v + d + n) % n);

  // Fresh signed link whenever the viewer opens or the shown photo changes.
  useEffect(() => {
    let alive = true;
    setUrl(null); setLoading(true); setFailed(false); setMsg(""); setScale(1); setOffset({ x: 0, y: 0 });
    sign(photos[i].storage_path).then((u) => {
      if (!alive) return;
      if (u) setUrl(u);
      else { setFailed(true); setLoading(false); setMsg(UNAVAILABLE); }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (n > 1 && e.key === "ArrowRight") go(1);
      if (n > 1 && e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = { mode: "pinch", dist: Math.hypot(a.x - b.x, a.y - b.y), scale };
    } else if (scale > 1) {
      gesture.current = { mode: "pan", x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    } else {
      gesture.current = { mode: "swipe", x: e.clientX, dx: 0 };
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (g.mode === "pinch" && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (g.dist) setScale(clamp(g.scale * (d / g.dist), 1, 4));
    } else if (g.mode === "pan") {
      setOffset({ x: g.ox + e.clientX - g.x, y: g.oy + e.clientY - g.y });
    } else if (g.mode === "swipe") {
      g.dx = e.clientX - g.x;
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (g.mode === "swipe" && pointers.current.size === 0 && n > 1 && Math.abs(g.dx || 0) > 60) go(g.dx < 0 ? 1 : -1);
    if (pointers.current.size === 0) gesture.current = {};
    else if (pointers.current.size < 2 && g.mode === "pinch") gesture.current = {};
  };

  const zoom = (d) => setScale((s) => {
    const next = clamp(Math.round((s + d) * 2) / 2, 1, 4);
    if (next === 1) setOffset({ x: 0, y: 0 });
    return next;
  });

  const download = async () => {
    const p = photos[i];
    setDlBusy(true); setMsg("");
    try {
      const fresh = await sign(p.storage_path);
      if (!fresh) throw new Error("sign");
      const res = await fetch(fresh);
      if (!res.ok) throw new Error("fetch");
      const blob = await res.blob();
      const ext = (p.storage_path.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `RealMatch-photo-${i + 1}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch {
      setMsg(UNAVAILABLE);
    } finally { setDlBusy(false); }
  };

  const ctrl = "rounded-full bg-white/10 p-2.5 text-white transition-colors hover:bg-white/25 disabled:opacity-30";
  return createPortal(
    <div data-testid="photo-viewer" className="fixed inset-0 z-[100] flex flex-col bg-black/95">
      <div className="flex items-center justify-between gap-2 p-3">
        <span data-testid="photo-viewer-counter" className="text-sm font-medium text-white/80">{i + 1} of {n}</span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => zoom(0.5)} disabled={scale >= 4} data-testid="photo-viewer-zoom-in" aria-label="Zoom in" className={ctrl}><ZoomIn className="h-5 w-5" /></button>
          <button type="button" onClick={() => zoom(-0.5)} disabled={scale <= 1} data-testid="photo-viewer-zoom-out" aria-label="Zoom out" className={ctrl}><ZoomOut className="h-5 w-5" /></button>
          <button type="button" onClick={download} disabled={dlBusy || failed} data-testid="photo-viewer-download" aria-label="Download photo" className={ctrl}>
            {dlBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
          </button>
          <button type="button" onClick={onClose} data-testid="photo-viewer-close" aria-label="Close viewer" className={ctrl}><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden"
        style={{ touchAction: "none" }}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
      >
        {loading && <Loader2 data-testid="photo-viewer-loading" className="absolute h-8 w-8 animate-spin text-[#D4AF37]" />}
        {failed ? (
          <UserRound data-testid="photo-viewer-placeholder" className="h-16 w-16 text-[#D4AF37]/60" />
        ) : url && (
          <img
            src={url} alt="" data-testid="photo-viewer-image" draggable={false}
            onLoad={() => setLoading(false)}
            onError={() => { setLoading(false); setFailed(true); setMsg(UNAVAILABLE); }}
            className="max-h-full max-w-full select-none object-contain"
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
          />
        )}
        {n > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} data-testid="photo-viewer-prev" aria-label="Previous photo" className={`${ctrl} absolute left-3 top-1/2 -translate-y-1/2`}><ChevronLeft className="h-6 w-6" /></button>
            <button type="button" onClick={() => go(1)} data-testid="photo-viewer-next" aria-label="Next photo" className={`${ctrl} absolute right-3 top-1/2 -translate-y-1/2`}><ChevronRight className="h-6 w-6" /></button>
          </>
        )}
      </div>
      {msg && <p data-testid="photo-viewer-error" className="pb-4 text-center text-sm text-[#F5E6BA]">{msg}</p>}
    </div>,
    document.body
  );
}
